import { Icon, SelectFilled } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { ReactNode } from 'react';

import { getLanguageDisplayName } from '@/utils/localization';

interface TranslationLanguagePickerProps {
    /** Every language the current field(s) can be translated into, the editing language already
     * excluded. */
    languages: string[];
    selectedLanguage: string;
    onSelectLanguage: (language: string) => void;
    isLanguageComplete: (language: string) => boolean;
    /** The field(s) for `selectedLanguage`, rendered inside the bordered panel below the header. */
    children: ReactNode;
}

/**
 * The language picker and completeness header shared by every "translate this per language" tab
 * (`ValueTranslationsTab`'s name/description/questions, `StrategicVisionTranslationsTab`'s
 * vision): a select listing each language with a completeness mark, then a bordered panel with
 * the selected language's name, a check/warning icon, a status label, and the caller's own
 * field(s) underneath.
 */
export function TranslationLanguagePicker({
    languages,
    selectedLanguage,
    onSelectLanguage,
    isLanguageComplete,
    children,
}: TranslationLanguagePickerProps) {
    const { t } = useTranslation();
    const isComplete = isLanguageComplete(selectedLanguage);

    return (
        <div className={'flex flex-col gap-6'}>
            <div className={'flex flex-col gap-2'}>
                <label className={'text-navy text-sm font-medium'}>
                    {t('visionValues.selectLanguage')}
                </label>

                <SelectFilled
                    value={selectedLanguage}
                    onChange={(value) => value && onSelectLanguage(value)}
                    options={languages.map((language) => {
                        const languageComplete = isLanguageComplete(language);
                        return {
                            value: language,
                            label: `${getLanguageDisplayName(language)} ${
                                languageComplete ? '✓' : `(${t('visionValues.incomplete')})`
                            }`,
                        };
                    })}
                    variant={'white'}
                    size={'md'}
                    className={'text-navy h-12'}
                    placeholder={t('visionValues.selectLanguage')}
                />
            </div>

            <div className={'!rounded-xl border border-gray-200 p-5'}>
                <div className={'mb-3 flex items-center justify-between'}>
                    <div className={'flex items-center gap-3'}>
                        <h3 className={'text-navy text-body-lg font-semibold'}>
                            {getLanguageDisplayName(selectedLanguage)}
                        </h3>

                        <Icon
                            name={isComplete ? 'essential-tick-circle' : 'essential-warning-2'}
                            size={'sm'}
                            colorClassName={isComplete ? 'text-green-500' : 'text-amber-500'}
                        />
                    </div>

                    <span className={'text-xs text-gray-500'}>
                        {isComplete ? t('visionValues.complete') : t('visionValues.incomplete')}
                    </span>
                </div>

                {children}
            </div>
        </div>
    );
}
